import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  TableCellLayout,
  Spinner,
  SearchBox,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { Add20Filled, EditRegular, DeleteRegular } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../../../Services/GraphServices";
import { GetAllAnalyticsProfiles, DeleteAnalyticsProfile } from "../../../Services/HeedifyServices";
import { useNotification } from "../../GenericComponent/NotificationSystem";
import { ConfirmAlert } from "../../GenericComponent/ConfirmAlert";
import InfoButton from "../../GenericComponent/InfoButton";
import "../../CSS/ListAnalyticsProfilesComponent.css";

const ListAnalyticsProfilesComponent = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts?.[0];
  const { showSuccess, showError, showWarning } = useNotification();
  const tenantId = props.ACTIVE_TENANT || activeAccount?.tenantId || null;

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [deletingProfileId, setDeletingProfileId] = useState(null);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [filterVisible, setFilterVisible] = useState({
    DISPLAY_NAME: false,
    SCOPE: false,
    QUICK_DROP_THRESHOLD: false,
    QUICK_ANSWER_THRESHOLD: false,
  });
  const [filters, setFilters] = useState({
    DISPLAY_NAME: "",
    SCOPE: "",
    QUICK_DROP_THRESHOLD: "",
    QUICK_ANSWER_THRESHOLD: "",
  });

  // Load profiles from Redux
  useEffect(() => {
    if (!props.ANALYTICS_PROFILES) return;
    
    setLoading(true);
    
    // Convert Redux object to array
    const profilesArray = Object.values(props.ANALYTICS_PROFILES);
    setProfiles(profilesArray);
    setFilteredProfiles(profilesArray);
    setLoading(false);
  }, [props.ANALYTICS_PROFILES]);

  // Apply filters
  const applyFilters = (rows, filters) => {
    return rows.filter((profile) => {
      const matchScope = profile.scope
        ?.toLowerCase()
        .includes(filters.SCOPE.toLowerCase());
      
      // Numeric filters - Utiliser reportConfig
      const matchQuickDrop = filters.QUICK_DROP_THRESHOLD === "" ||
        (profile.reportConfig?.thresholds?.quickDropThreshold ?? 6) === Number(filters.QUICK_DROP_THRESHOLD);
      const matchQuickAnswer = filters.QUICK_ANSWER_THRESHOLD === "" ||
        (profile.reportConfig?.thresholds?.quickAnswerThreshold ?? 30) === Number(filters.QUICK_ANSWER_THRESHOLD);
      
      return  matchScope && matchQuickDrop && matchQuickAnswer;
    });
  };

  useEffect(() => {
    const newFiltered = applyFilters(profiles, filters);
    setFilteredProfiles(newFiltered);
  }, [filters, profiles]);

  // Sorting
  const columns = [
  
    createTableColumn({
      columnId: "SCOPE",
      compare: (a, b) => a.scope?.localeCompare(b.scope || "") ?? 0,
    }),
    createTableColumn({ columnId: "QUICK_DROP_THRESHOLD" }),
    createTableColumn({ columnId: "QUICK_ANSWER_THRESHOLD" }),
    createTableColumn({ columnId: "ACTIONS" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures({ columns, items: filteredProfiles }, [
    useTableSort({
      defaultSortState: { sortColumn: "DISPLAY_NAME", sortDirection: "ascending" },
    }),
  ]);

  const rows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({ ...prev, [columnKey]: value }));
  };

  const toggleFilterVisibility = (column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const handleAddNew = () => {
    navigate(`/analyticsProfile/newProfile`);
  };

  const handleEdit = (profileId) => {
    navigate(`/analyticsProfile/${profileId}`);
  };

  const confirmRemoveProfile = (profile, rowId) => {
    setSelectedProfile({
      scope: profile.scope,
      id: profile.id,
      key: rowId,
    });
  };

  const removeProfile = (rowId, profileId) => {
    setDeletingProfileId(profileId);
    getAccessToken(instance, activeAccount)
      .then((token) => DeleteAnalyticsProfile(tenantId, profileId, token))
      .then((result) => {
        if (result.success) {
          showSuccess(t("ANALYTICS_PROFILE_DELETED_SUCCESS"));
          setProfiles(profiles.filter(p => p.id !== profileId));
          
          // Update Redux
          const updatedProfiles = { ...props.ANALYTICS_PROFILES };
          delete updatedProfiles[profileId];
          props.setAnalyticsProfiles(updatedProfiles);
        } else {
          showError(t("ANALYTICS_PROFILE_DELETE_FAILED"));
        }
      })
      .catch((err) => {
        console.error("❌ Error deleting analytics profile:", err);
        showError(t("ANALYTICS_PROFILE_DELETE_ERROR"));
      })
      .finally(() => {
        setDeletingProfileId(null);
        setSelectedProfile(null);
      });
  };

 

  return (
    <div className="analytics-profiles-list-container">
      <div className="analytics-profiles-header">
        <h2>{t("ANALYTICS_PROFILES")}</h2>
        <div className="analytics-profiles-actions">
          <Button
            icon={<Add20Filled />}
            appearance="primary"
            onClick={handleAddNew}
          >
            {t("ADD_ANALYTICS_PROFILE")}
          </Button>
        </div>
      </div>

      <Table sortable>
        <TableHeader>
          <TableRow>
          

            {/* SCOPE */}
            <TableHeaderCell {...headerSortProps("SCOPE")}>
              <div className="agent-profile-list-header-filter-container">
                <div className="agent-profile-list-div-filter-style">
                  {t("SCOPE")}
                  <CiFilter
                    className="agent-profile-list-filter-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilterVisibility("SCOPE");
                    }}
                  />
                  {filterVisible.SCOPE && (
                    <Input
                      className="agent-profile-list-input-filter-inline"
                      value={filters.SCOPE}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("SCOPE", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
            </TableHeaderCell>

            {/* QUICK_DROP_THRESHOLD */}
            <TableHeaderCell {...headerSortProps("QUICK_DROP_THRESHOLD")}>
              <div className="agent-profile-list-header-filter-container">
                <div className="agent-profile-list-div-filter-style">
                  {t("QUICK_DROP")}
                  <InfoButton infoKey="QUICK_DROP_THRESHOLD" category="ANALYTICS" />
                  <CiFilter
                    className="agent-profile-list-filter-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilterVisibility("QUICK_DROP_THRESHOLD");
                    }}
                  />
                  {filterVisible.QUICK_DROP_THRESHOLD && (
                    <Input
                      type="number"
                       min={0}
                      className="agent-profile-list-input-filter-inline"
                      value={filters.QUICK_DROP_THRESHOLD}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("QUICK_DROP_THRESHOLD", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
            </TableHeaderCell>

            {/* QUICK_ANSWER_THRESHOLD */}
            <TableHeaderCell {...headerSortProps("QUICK_ANSWER_THRESHOLD")}>
              <div className="agent-profile-list-header-filter-container">
                <div className="agent-profile-list-div-filter-style">
                  {t("QUICK_ANSWER")}
                  <InfoButton infoKey="QUICK_ANSWER_THRESHOLD" category="ANALYTICS" />
                  <CiFilter
                    className="agent-profile-list-filter-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFilterVisibility("QUICK_ANSWER_THRESHOLD");
                    }}
                  />
                  {filterVisible.QUICK_ANSWER_THRESHOLD && (
                    <Input
                      type="number"
                      min={0}
                      className="agent-profile-list-input-filter-inline"
                      value={filters.QUICK_ANSWER_THRESHOLD}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, data) =>
                        handleFilterChange("QUICK_ANSWER_THRESHOLD", data.value)
                      }
                      placeholder={t("FILTER")}
                      size="small"
                    />
                  )}
                </div>
              </div>
            </TableHeaderCell>

            <TableHeaderCell className="agent-profile-list-cell-right">{t("ACTIONS")}</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ item: profile }, index) => (
            <TableRow key={profile.id}>
            
              <TableCell>
                <TableCellLayout>{profile.scope}</TableCellLayout>
              </TableCell>
              <TableCell>
                <TableCellLayout>{profile.reportConfig?.thresholds?.quickDropThreshold ?? 6} </TableCellLayout>
              </TableCell>
              <TableCell>
                <TableCellLayout>{profile.reportConfig?.thresholds?.quickAnswerThreshold ?? 30} </TableCellLayout>
              </TableCell>
              <TableCell>
                <TableCellLayout>
                  <Button
                    appearance="subtle"
                    icon={<EditRegular />}
                    aria-label="Edit"
                    onClick={() => handleEdit(profile.id)}
                  />
                  <Button
                    appearance="subtle"
                    icon={deletingProfileId === profile.id ? <Spinner size="tiny" /> : <DeleteRegular />}
                    aria-label="Delete"
                    onClick={() => confirmRemoveProfile(profile, index)}
                    disabled={deletingProfileId === profile.id}
                  />
                </TableCellLayout>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {filteredProfiles.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>{t("NO_ANALYTICS_PROFILES_FOUND")}</p>
        </div>
      )}

      {selectedProfile && (
        <ConfirmAlert
          open={!!selectedProfile}
          name={selectedProfile.scope}
          onConfirm={() => removeProfile(selectedProfile.key, selectedProfile.id)}
          onCancel={() => setSelectedProfile(null)}
        />
      )}
    </div>
  );
};

export default ListAnalyticsProfilesComponent;
